import { shell } from 'electron';
import { ApiError, apiRequest } from './api';
import { getSupabase } from './supabase/client';
import { getSettings, saveSettings } from './settings';
import { t } from './language';
import type { ConsentKind, LegalDocumentDTO, LegalLinksDTO, OnboardingStateDTO } from '../shared/types';

/**
 * Girişten sonraki ilk kurulum ve yasal onaylar.
 *
 * İki ayrı soru var:
 *  - Yasal onay HESABA bağlı ve sunucuda tutuluyor (`/api/v1/consents`,
 *    user_consents tablosu). Metnin sürümü değişince uygulama yeniden onay ister (tam ekran
 *    kapı); işleme uçları yalnızca hiç onay vermemiş hesapta 403 döner.
 *  - Kurulum ("Hazırsın" karşılaması, ardından ürün turu) CİHAZA bağlı: aynı hesap
 *    yeni bir cihazda açılınca karşılama ve tur yeniden gösterilir.
 */

interface ConsentsResponse {
  accepted: Partial<Record<ConsentKind, string>>;
  pending: LegalDocumentDTO[];
}

const CONSENT_KINDS: readonly ConsentKind[] = ['terms', 'privacy', 'recording', 'transfer'];

function isConsentKind(value: unknown): value is ConsentKind {
  return typeof value === 'string' && (CONSENT_KINDS as readonly string[]).includes(value);
}

async function currentUserId(): Promise<string> {
  const { data } = await getSupabase().auth.getSession();
  const id = data.session?.user.id;
  if (!id) throw new Error(t('Oturum bulunamadı — yeniden giriş yapın.', 'No active session — please sign in again.'));
  return id;
}

async function rememberAccepted(userId: string, accepted: ConsentsResponse['accepted']): Promise<void> {
  const versions: Record<string, string> = {};
  for (const [kind, version] of Object.entries(accepted ?? {})) {
    if (isConsentKind(kind) && typeof version === 'string') versions[kind] = version;
  }
  const others = (await getSettings()).legalAccepted.filter((entry) => entry.userId !== userId);
  await saveSettings({ legalAccepted: [...others, { userId, versions }] });
}

/** Sunucudan gelen belge listesini yalnızca bildiğimiz alanlarla geçir. */
function toDocuments(raw: unknown): LegalDocumentDTO[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const doc = (item ?? {}) as Partial<LegalDocumentDTO>;
    if (!isConsentKind(doc.kind) || typeof doc.version !== 'string' || typeof doc.url !== 'string') return [];
    const title = { tr: String(doc.title?.tr ?? doc.kind), en: String(doc.title?.en ?? doc.kind) };
    return [{ kind: doc.kind, version: doc.version, url: doc.url, title }];
  });
}

export async function getOnboardingState(): Promise<OnboardingStateDTO> {
  const userId = await currentUserId();
  const settings = await getSettings();
  const needsSetup = !settings.onboardedUsers.includes(userId);

  try {
    const res = await apiRequest<ConsentsResponse>('GET', '/api/v1/consents');
    await rememberAccepted(userId, res.accepted);
    return { pending: toDocuments(res.pending), needsSetup, offline: false };
  } catch (err) {
    // Sunucuya ulaşılamıyorsa (ağ yok, 5xx) ve bu hesap bu bilgisayarda daha
    // önce onay verdiyse uygulama açılsın; işleme uçları zaten sunucuda korunuyor.
    const unreachable = err instanceof ApiError && (err.status === 0 || err.status >= 500);
    const cached = settings.legalAccepted.find((entry) => entry.userId === userId);
    if (unreachable && cached && Object.keys(cached.versions).length > 0) {
      return { pending: [], needsSetup, offline: true };
    }
    throw err;
  }
}

/**
 * Kullanıcının ekranda gördüğü sürümleri onaylar. Arada metin güncellendiyse
 * sunucu 409 döner; renderer durumu yeniden okuyup yeni sürümü gösterir.
 */
export async function acceptLegal(input: unknown): Promise<void> {
  const items = Array.isArray(input) ? input : [];
  const accept = items.flatMap((item) => {
    const entry = (item ?? {}) as { kind?: unknown; version?: unknown };
    return isConsentKind(entry.kind) && typeof entry.version === 'string' && entry.version.length <= 40
      ? [{ kind: entry.kind, version: entry.version }]
      : [];
  });
  if (accept.length === 0) throw new Error(t('Onaylanacak metin seçilmedi.', 'No document selected to accept.'));

  const userId = await currentUserId();
  const res = await apiRequest<Pick<ConsentsResponse, 'accepted'>>('POST', '/api/v1/consents', { body: { accept } });
  await rememberAccepted(userId, res.accepted);
}

/** Bu cihazdaki ilk kurulum bitti ("Hazırsın" karşılaması bir daha gösterilmez; ürün turu bir kez açılır). */
export async function completeSetup(): Promise<void> {
  const userId = await currentUserId();
  const { onboardedUsers } = await getSettings();
  if (!onboardedUsers.includes(userId)) await saveSettings({ onboardedUsers: [...onboardedUsers, userId] });
}

/** Tanıtım sitesi (tanitim-sitesi/) — yasal sayfaların herkese açık adresi. */
const SITE_URL = 'https://talknote.net';

/**
 * Ayarlar > Yardım al ve giriş ekranındaki yasal bağlantılar tanıtım sitesine
 * gider; oturum gerektirmez. Onay penceresindeki bağlantılar ise sunucudan
 * (/api/v1/consents) gelir: onaylanan sürümün metni orada.
 */
export function getLegalLinks(): LegalLinksDTO {
  return {
    terms: `${SITE_URL}/kullanim-kosullari`,
    privacy: `${SITE_URL}/gizlilik`,
    distanceSales: `${SITE_URL}/mesafeli-satis-sozlesmesi`,
    delivery: `${SITE_URL}/teslimat-ve-iade`,
    disclaimer: `${SITE_URL}/sorumluluk-reddi`,
    participants: `${SITE_URL}/katilimci-aydinlatma`,
  };
}

/**
 * Windows'ta "Gizlilik > Mikrofon" ayarını açar: masaüstü uygulamalarının
 * mikrofon erişimi kapalıysa kayıt sessiz kalır. `openExternalSafely` bu şemayı
 * kabul etmediği için ayrı ve sabit adresli bir kanal.
 */
export async function openMicPrivacySettings(): Promise<boolean> {
  if (process.platform !== 'win32') return false;
  await shell.openExternal('ms-settings:privacy-microphone');
  return true;
}
